import { Button, Form, InputGroup, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import ErrorDisplay from "../common/ErrorDisplay";
import LoadingDisplay from "../common/LoadingDisplay";
import {
  ChevronExpand,
  Copy,
  PlusCircleFill,
  Search,
  TrashFill,
} from "react-bootstrap-icons";
import { useEffect, useState } from "react";
import {
  AttributeTextStringModel,
  glossaryAttributeTextStringApi,
} from "../api/glossaryAttributeTextStringApi";
import { Problem } from "../api/backendApi";
import { authUtils } from "../common/authUtils";
import { useAuth } from "react-oidc-context";
import ContextMenuCopy from "../components/contextmenus/ContextMenuCopy";
import {
  TableColumnSorter,
  Order,
} from "../common/tableColumnSorting/tableColumnSorter";
import ArraySort from "../common/tableColumnSorting/arraySort";
import GlossaryTextModal from "../components/modals/GlossaryTextModal";

function AttributeTextStringPage() {
  const auth = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [attributeTextStrings, setAttributeTextStrings] = useState<
    AttributeTextStringModel[]
  >([]);
  const [columnSort, setColumnSort] = useState<
    TableColumnSorter<AttributeTextStringModel> | undefined
  >(undefined);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [filter, setFilter] = useState<string>("");
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [isCopy, setIsCopy] = useState<boolean>(false);
  const [show, setShow] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<number | undefined>(undefined);
  const [modalText, setModalText] = useState<string>("");
  const [newAttributeTextString, setNewAttributeTextString] = useState<
    AttributeTextStringModel | undefined
  >(undefined);
  const [attributeTextStringBeingEdited, setAttributeTextStringBeingEdited] =
    useState<AttributeTextStringModel | undefined>(undefined);
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [contextMenuSelected, setContectMenuSelected] =
    useState<AttributeTextStringModel>();
  const [contextMenuOptions, setContectMenuOptions] = useState<string[]>([]);

  useEffect(() => {
    async function loadAttributeTextStrings() {
      setLoading(true);
      const data =
        await glossaryAttributeTextStringApi.getAttributeTextStrings();

      if (data.ok) {
        setError(undefined);
        setAttributeTextStrings(data.value);
      } else {
        setError(data.problem);
      }

      setLoading(false);
    }

    loadAttributeTextStrings();
  }, []);

  const applyColumnSort = (attributeTexts: AttributeTextStringModel[]) => {
    if (columnSort === undefined) {
      return attributeTexts;
    }

    return [...attributeTexts].sort(columnSort.compareFn);
  };

  const handleShow = (
    isCreate: boolean,
    modalText: string,
    attributeTextString?: AttributeTextStringModel,
    isCopy: boolean = false,
  ) => {
    setModalText(modalText);
    setIsCreate(isCreate);
    setIsCopy(isCopy);
    setAttributeTextStringBeingEdited(attributeTextString);
    setNewAttributeTextString(attributeTextString);
    setShow(true);
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLTableRowElement>) => {
    event.preventDefault();

    const position = {
      top: event.clientY,
      left: event.clientX,
    };

    setContextMenuPosition(position);
    setIsContextMenuVisible(true);
  };

  const deleteAttributeTextString = (
    attributeTextString: AttributeTextStringModel,
  ) => {
    glossaryAttributeTextStringApi
      .deleteAttributeTextString(attributeTextString.stringId)
      .then((response) => {
        if (response.ok) {
          setAttributeTextStrings(
            attributeTextStrings.filter((a) => a !== attributeTextString),
          );
        } else {
          setError(response.problem);
        }
      });
  };

  const handleSubmit = (standardTextString: AttributeTextStringModel) => {
    let call = isCreate
      ? glossaryAttributeTextStringApi.createAttributeTextString(
          standardTextString,
        )
      : glossaryAttributeTextStringApi.updateAttributeTextString(
          standardTextString.stringId,
          standardTextString,
        );

    return call;
  };

  const onNewAttributeTextString = (
    attributeTextString: AttributeTextStringModel,
  ) => {
    if (
      attributeTextStrings.find(
        (a) => a.stringId === attributeTextString.stringId,
      )
    ) {
      let updatedAttributeTextString = attributeTextStrings.map(
        (a: AttributeTextStringModel) => {
          return a.stringId === attributeTextString?.stringId
            ? {
                searchOrder: attributeTextString.searchOrder,
                stringId: attributeTextString.stringId,
                english: attributeTextString.english,
                dutch: attributeTextString.dutch,
                german: attributeTextString.german,
                french: attributeTextString.french,
                spanish: attributeTextString.spanish,
                portuguese: attributeTextString.portuguese,
                italian: attributeTextString.italian,
                polish: attributeTextString.polish,
                czech: attributeTextString.czech,
                swedish: attributeTextString.swedish,
                danish: attributeTextString.danish,
                norwegian: attributeTextString.norwegian,
                finnish: attributeTextString.finnish,
                hungarian: attributeTextString.hungarian,
                croatian: attributeTextString.croatian,
                hebrew: attributeTextString.hebrew,
                slovene: attributeTextString.slovene,
                russian: attributeTextString.russian,
                arabic: attributeTextString.arabic,
                ukrainian: attributeTextString.ukrainian,
                isNew: attributeTextString.isNew,
              }
            : a;
        },
      );

      setAttributeTextStrings(updatedAttributeTextString);
    } else {
      setAttributeTextStrings([...attributeTextStrings, attributeTextString]);
    }

    setNewAttributeTextString(undefined);
  };

  const onAttributeTextStringChanged = (
    attributeTextString: AttributeTextStringModel,
  ) => {
    setError(undefined);
    let updatedAttributeTextString = attributeTextStrings.map(
      (a: AttributeTextStringModel) => {
        return a === attributeTextStringBeingEdited ? attributeTextString : a;
      },
    );

    setAttributeTextStrings(updatedAttributeTextString);
    setAttributeTextStringBeingEdited(undefined);
  };

  const handleColumnSort = (
    columnName: string,
    sortFunction: (
      sortOrder: Order,
    ) => (a: AttributeTextStringModel, b: AttributeTextStringModel) => number,
  ) => {
    const sortOrder: Order =
      columnSort !== undefined &&
      columnSort.columnName === columnName &&
      columnSort.sortBy === "asc"
        ? "desc"
        : "asc";

    setColumnSort({
      columnName,
      compareFn: sortFunction(sortOrder),
      sortBy: sortOrder,
    });
  };

  const copyToClipboard = async(text: string) => {
    await navigator.clipboard.writeText(text.replaceAll("<NL>", "\r\n"));
  }

  return (
    <>
      <div className="main-container-title">
        <h1
          style={{
            alignItems: "center",
            justifyContent: "left",
            display: "flex",
          }}
        >
          Glossary Attribute Texts
          {authUtils.isAdministrator(auth) && (
            <PlusCircleFill
              className="text-primary"
              onClick={() => handleShow(true, "Add Attribute Text", undefined)}
              width={32}
              height={32}
              style={{ marginLeft: "0.5rem" }}
              role="button"
            />
          )}
        </h1>
        <ErrorDisplay error={error} />
      </div>
      <div className="main-container-content">
        <LoadingDisplay loading={loading} />

        {!loading && (
          <div style={{ display: "flex", flexDirection: "row" }}>
            <InputGroup
              className="sm mb-2 mt-1"
              style={{
                display: "flex",
                width: "fit-content",
                marginRight: "1rem",
              }}
            >
              <span
                className="input-group-text"
                style={{ display: "flex", flexDirection: "row" }}
              >
                <Search width={12} height={12} />
                <Form.Label
                  style={{
                    fontWeight: "400",
                    fontSize: "16px",
                    display: "flex",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginLeft: "5px",
                  }}
                >
                  Filter
                </Form.Label>
              </span>
              <Form.Control
                size="sm"
                onChange={(e) => {
                  setFilter(e.target.value);
                }}
                style={{ width: "400px" }}
              ></Form.Control>
            </InputGroup>
          </div>
        )}
        {!loading && (
          <div
            className="shadow p-3 bg-white rounded"
            style={{ height: "95%" }}
          >
            <div className="fixed-table-header" style={{ height: "100%" }}>
              <Table hover striped>
                <thead>
                  <tr>
                    <th style={{ width: "10%" }}>
                      <Form.Label
                        style={{
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "center",
                          maxWidth: "fit-content",
                        }}
                        onClick={() => {
                          handleColumnSort("StringId", (sortOrder) =>
                            ArraySort.byNumber(
                              (attrText) => attrText.stringId ?? 0,
                              sortOrder,
                            ),
                          );
                        }}
                        className="table-header"
                      >
                        String Id
                        <ChevronExpand
                          className="text-primary"
                          style={{
                            display: "flex",
                            margin: "1px",
                            borderLeft: "1px solid #0d6efd",
                            marginLeft: "5px",
                          }}
                        />
                      </Form.Label>
                    </th>
                    <th style={{ width: "10%" }}>
                      <Form.Label
                        style={{
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={() => {
                          handleColumnSort("SearchOrder", (sortOrder) =>
                            ArraySort.byNumber(
                              (attrText) => attrText.searchOrder ?? 0,
                              sortOrder,
                            ),
                          );
                        }}
                        className="table-header"
                      >
                        Search Order
                        <ChevronExpand
                          className="text-primary"
                          style={{
                            display: "flex",
                            margin: "1px",
                            borderLeft: "1px solid #0d6efd",
                            marginLeft: "5px",
                          }}
                        />
                      </Form.Label>
                    </th>
                    <th>
                    </th>
                    <th>
                      <Form.Label
                        style={{
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={() => {
                          handleColumnSort("English", (sortOrder) =>
                            ArraySort.byString(
                              (attrText) => attrText.english,
                              sortOrder,
                            ),
                          );
                        }}
                        className="table-header"
                      >
                        English
                        <ChevronExpand
                          className="text-primary"
                          style={{
                            display: "flex",
                            margin: "1px",
                            borderLeft: "1px solid #0d6efd",
                            marginLeft: "5px",
                          }}
                        />
                      </Form.Label>
                    </th>
                    <th style={{ width: "10%" }}>
                      <Form.Label
                        style={{
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={() => {
                          handleColumnSort("IsNew", (sortOrder) =>
                            ArraySort.byNumber(
                              (attrText) => (attrText.isNew ? 1 : 0),
                              sortOrder,
                            ),
                          );
                        }}
                        className="table-header"
                      >
                        Is New
                        <ChevronExpand
                          className="text-primary"
                          style={{
                            display: "flex",
                            margin: "1px",
                            borderLeft: "1px solid #0d6efd",
                            marginLeft: "5px",
                          }}
                        />
                      </Form.Label>
                    </th>
                    <th style={{ width: "1px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {applyColumnSort(
                    attributeTextStrings.filter(
                      (attributeTextString) =>
                        attributeTextString.english
                          ?.toUpperCase()
                          .includes(filter.toUpperCase()),
                    ),
                  ).map((attributeTextString) => (
                    <tr
                      className="table-row-scale"
                      role="button"
                      key={attributeTextString.stringId}
                      onClick={() => {
                        if (authUtils.isAdministrator(auth)) {
                          handleShow(
                            false,
                            "Edit Attribute Text",
                            attributeTextString,
                          );
                        }
                      }}
                      onContextMenu={(e) => {
                        handleContextMenu(e);
                        setContectMenuSelected(attributeTextString);
                        let options: string[] = ["Create Copy"];

                        setContectMenuOptions(options);
                      }}
                    >
                      <td style={{ fontWeight: "600" }}>
                        {attributeTextString.stringId?.toString()}
                      </td>
                      <td>{attributeTextString.searchOrder?.toString()}</td>
                      <td>
                        <OverlayTrigger
                          placement="right"
                          show={showToast === attributeTextString.stringId}
                          delay={{ show: 0, hide: 0 }}
                          overlay={
                            <Tooltip id="tooltip-right">
                              <strong>Copied!</strong>
                            </Tooltip>
                          }
                          >
                          <Button variant="outline-primary" size="sm" onClick={(e) => {
                            e.stopPropagation();
                            setShowToast(attributeTextString.stringId!);
                            copyToClipboard(attributeTextString.english!);
                            setTimeout(() => setShowToast(undefined), 1500);
                            }}>
                              <Copy />
                          </Button>
                        </OverlayTrigger>                       
                      </td>
                      <td className="table-data-width" style={{whiteSpace: "pre"}}>
                        {attributeTextString.english}
                      </td>
                      <td>{attributeTextString.isNew ? "Yes" : "No"}</td>
                      <td>
                        {authUtils.isAdministrator(auth) && (
                          <TrashFill
                            className="text-danger"
                            height={24}
                            width={24}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                window.confirm(
                                  "Are you sure this text can be deleted?",
                                )
                              ) {
                                deleteAttributeTextString(attributeTextString);
                              }
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </div>
      {show && authUtils.isAdministrator(auth) ? (
        <GlossaryTextModal
          glossaryText={
            newAttributeTextString ?? {
              stringId: undefined,
              searchOrder: undefined,
              english: undefined,
              dutch: undefined,
              german: undefined,
              french: undefined,
              spanish: undefined,
              portuguese: undefined,
              italian: undefined,
              polish: undefined,
              czech: undefined,
              swedish: undefined,
              danish: undefined,
              norwegian: undefined,
              finnish: undefined,
              hungarian: undefined,
              croatian: undefined,
              hebrew: undefined,
              slovene: undefined,
              russian: undefined,
              arabic: undefined,
              ukrainian: undefined,
            }
          }
          searchOrders={attributeTextStrings.map((a) => a.searchOrder)}
          isCreate={isCreate}
          isCopy={isCopy}
          onSubmit={handleSubmit}
          onChange={
            isCreate ? onNewAttributeTextString : onAttributeTextStringChanged
          }
          onCancel={() => {
            setNewAttributeTextString(undefined);
            setAttributeTextStringBeingEdited(undefined);
            setShow(false);
          }}
          modalText={modalText}
          type="attribute"
        />
      ) : null}
      {isContextMenuVisible && authUtils.isAdministrator(auth) && (
        <ContextMenuCopy
          onClose={() => setIsContextMenuVisible(false)}
          onCreateCopy={() =>
            handleShow(
              true,
              "Add Attribute text",
              {
                stringId: undefined,
                searchOrder: undefined,
                english: contextMenuSelected?.english,
                dutch: contextMenuSelected?.dutch,
                german: contextMenuSelected?.german,
                french: contextMenuSelected?.french,
                spanish: contextMenuSelected?.spanish,
                portuguese: contextMenuSelected?.portuguese,
                italian: contextMenuSelected?.italian,
                polish: contextMenuSelected?.polish,
                czech: contextMenuSelected?.czech,
                swedish: contextMenuSelected?.swedish,
                danish: contextMenuSelected?.danish,
                norwegian: contextMenuSelected?.norwegian,
                finnish: contextMenuSelected?.finnish,
                hungarian: contextMenuSelected?.hungarian,
                croatian: contextMenuSelected?.croatian,
                hebrew: contextMenuSelected?.hebrew,
                slovene: contextMenuSelected?.slovene,
                russian: contextMenuSelected?.russian,
                arabic: contextMenuSelected?.arabic,
                ukrainian: contextMenuSelected?.ukrainian,
              },
              true,
            )
          }
          options={contextMenuOptions}
          style={{
            position: "absolute",
            top: contextMenuPosition.top,
            left: contextMenuPosition.left,
          }}
        />
      )}
    </>
  );
}

export default AttributeTextStringPage;
